/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

angular.module('prism-ui.components').service('MediaInfoService', function (
    this: JS5Object,
    _: JS5Object,
    $q: JS5Object,
    $timeout: JS5Function,
    $window: JS5Object
) {
    // try to read 4 MiB at a time
    const CHUNK_SIZE = 4 * (2 ** 20);
    // const CHUNK_SIZE = 256 * 1024;

    //
    // These are defined in the MediaInfoLib sources.
    //
    enum MediaInfoStates {
        NONE = 0x00,
        ACCEPTED = 0x01,
        FILLED = 0x02,
        UPDATE = 0x04,
        FINALIZED = 0x08
    }

    let mediaInfo: JS5Object = null;
    let mediaInfoDeferred: JS5Object = null;
    const loadMediaInfo = () => {

        if (!mediaInfoDeferred) {
            mediaInfoDeferred = $q.defer();
            console.info(`Loading MediaInfo.js ...`);
            try {
                $window.MediaInfo(function (this: JS5Object) {
                    try {

                        //
                        // Note: Errors thrown here will not bubble
                        // back up to the outer scope, hence why
                        // there's a nested try/catch.
                        //

                        //
                        // Note 2: Here, `this` is the local
                        // anonymous function's scope, not the
                        // outer service's scope.
                        //

                        mediaInfo = new this.MediaInfo();
                        mediaInfoDeferred.resolve(mediaInfo);
                    } catch (errorData) {
                        mediaInfoDeferred.reject(errorData);
                    }
                });
            } catch (errorData) {
                mediaInfoDeferred.reject(errorData);
            }
        }

        return mediaInfoDeferred.promise;
    };

    const parseFile = (file: File, deferredMediaInfoExecution: JS5Object) => {
        // console.log(`MediaInfoService.parseFile(): parsing file ${file.name}`);
        let fileUploaderConfig = {
            processing: false,
            fileSize: 0,
            offset: 0,
            state: 0
        };

        if (fileUploaderConfig.processing) {
            console.info(`MediaInfoService.parseFile(): returning; already processing`);
            return;
        }
        fileUploaderConfig.processing = true;

        fileUploaderConfig.fileSize = file.size;

        mediaInfo.open_buffer_init(fileUploaderConfig.fileSize, fileUploaderConfig.offset);

        let fileReader: FileReader;
        const seek = (length: number) => {
            // console.log(`MediaInfoService.parseFile(): seeking to ${length} bytes`);
            if (!fileReader) {
                fileReader = new FileReader();
                fileReader.onload = processChunk;
            }

            if (fileUploaderConfig.processing) {
                let blob = file.slice(fileUploaderConfig.offset, length + fileUploaderConfig.offset);
                fileReader.readAsArrayBuffer(blob);
            } else {
                console.info(`MediaInfoService.parseFile(): no longer processing - closing mediaInfo`);
                mediaInfo.close();
            }
        };

        const openBufferContinueGotoGet = (chunk: Uint8Array, chunkLength: number) => {
            //
            // This is required because the emscripten/JS bindings
            // do not support >32-bit integers.
            //
            let seekTo: number = -1;

            let seekToLow: number = mediaInfo.open_buffer_continue_goto_get_lower();
            let seekToHigh: number = mediaInfo.open_buffer_continue_goto_get_upper();
            if (seekToLow === -1 && seekToHigh === -1) {
                seekTo = -1;
            } else if (seekToLow < 0) {
                seekTo = seekToLow + 2**32 + (seekToHigh * 2**32);
            } else {
                seekTo = seekToLow + (seekToHigh * 2**32);
            }

            return seekTo;
        }

        const finalize = () => {
            console.info(`MediaInfoService.parseFile(): analysis complete; obtaining result`);
            mediaInfo.open_buffer_finalize();
            let resultXML = mediaInfo.inform();
            mediaInfo.close();

            // add file name and extension to track 1
            let fileName = file.name;
            let extension = _.chain(fileName).split('.').last().value();
            resultXML = resultXML.replace(
                /<\/track>/gi,
                `<FileName>${fileName}</FileName>\n` +
                `<FileExtension>${extension}</FileExtension>\n` +
                `</track>`
            );

            // form the final XML, adding an outer "Mediainfo"
            // let mediaInfoXML =
            //     `<Mediainfo version="${this.mediaInfoVersion}">\n` +
            //     `${resultXML}\n` +
            //     `</Mediainfo>`;

            deferredMediaInfoExecution.resolve(resultXML);
        }

        const processChunk = (event: JS5Object) => {
            if (!_.isNil(event.target.error)) {
                let message = `MediaInfo.JS failed: ${event.target.error}`;
                mediaInfo.close();
                deferredMediaInfoExecution.reject(message);
                return;
            }

            let chunk = new Uint8Array(event.target.result);
            fileUploaderConfig.state = mediaInfo.open_buffer_continue(chunk, chunk.length);

            if (chunk.length == 0 || !!(fileUploaderConfig.state & MediaInfoStates.FINALIZED)) {
                finalize();
                fileUploaderConfig.processing = false;
                return;
            }

            let seekTo = openBufferContinueGotoGet(chunk, chunk.length);
            if (seekTo === -1) {
                fileUploaderConfig.offset += chunk.length;
            } else {
                fileUploaderConfig.offset = seekTo;
                mediaInfo.open_buffer_init(fileUploaderConfig.fileSize, seekTo);
            }

            chunk = null;
            seek(CHUNK_SIZE);
        };

        // start
        seek(CHUNK_SIZE);
    };


    this.executeMediaInfo = (file: File) => {
        console.log(`MediaInfoService.executeMediaInfo(): executing MediaInfo on file ${file.name}`)
        return loadMediaInfo().then(() => {
            if (!this.mediaInfoVersion) {
                this.mediaInfoVersion = mediaInfo.get_library_version();
                console.info(`MediaInfo.js loaded -- using MediaInfoLib ${this.mediaInfoVersion}`);
            }
            let deferredMediaInfoExecution = $q.defer();
            $timeout(() => {
                try {
                    parseFile(file, deferredMediaInfoExecution);
                } catch (errorData) {
                    deferredMediaInfoExecution.reject(errorData);
                }
            });

            return deferredMediaInfoExecution.promise;
        });

    };

    this.executeMediaInfoOnAsperaObjects = (asperaUploadObjects: JS5Object[]) => {
        console.log(`MediaInfoService.executeMediaInfoOnAsperaObjects(): executing MediaInfo on ${asperaUploadObjects.length} Aspera objects`);
        let deferredMediaInfoExecutions: JS5Object = {};
        _.forEach(asperaUploadObjects, (asperaObjectAndFile: JS5Object) => {
            deferredMediaInfoExecutions[asperaObjectAndFile.name] = this.executeMediaInfo(asperaObjectAndFile.file)
                .then((mediaInfoXML: string) => {
                    asperaObjectAndFile.mediaInfoXML = mediaInfoXML;
                    return asperaObjectAndFile;
                });
        });

        return $q.all(deferredMediaInfoExecutions);
    };

});
